import React from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ArrowDownIcon, ArrowUpIcon } from 'components/icons/index';
import {
  formatCashWithAbbreviation,
  formatWithCommas,
  OWNER,
  SUPPORT_OFFICER,
} from 'libs';
import Fetching from 'components/Fetching';
import classname from 'classname';
import { useSelector } from 'react-redux';
import Button from 'components/button';
import { globalStyles } from 'styles';
import TopUpModal from './modals/top-up';
import WithdrawModal from './modals/withdrawal-modal';
import styles from './dashboard-chart.module.scss';

const Chart = ({ data, loadingGraphData }) => (
  <>
    {loadingGraphData ? (
      <Fetching height="300px" />
    ) : (
      <ResponsiveContainer
        width="100%"
        height={300}
        minWidth="0"
        className={styles.responsiveContainer}
      >
        <AreaChart
          data={data}
          margin={{ top: 10, right: 0, left: 0, bottom: 0 }}
        >
          <defs>
            <linearGradient
              id="colorUv"
              x1="0"
              y1="0"
              x2="0"
              y2="1"
              rotate="180deg"
            >
              <stop offset="0%" stopColor="#ACB2FD" stopOpacity={0.096} />
              <stop offset="60%" stopColor="#ACB2FD" stopOpacity={0} />
            </linearGradient>
          </defs>
          <YAxis
            dataKey="amount"
            axisLine={false}
            tickLine={false}
            tickFormatter={(value) => `₦${formatCashWithAbbreviation(value)}`}
            padding={{ left: 0, right: 0 }}
            tick={{ fill: '#748CAD', textTransform: 'upppercase' }}
            style={{
              fontSize: '12px',
              fontFamily: 'Sofia Pro',
              color: '#748CAD',
              fontWeight: '500',
              textTransform: 'upppercase',
            }}
          />
          <XAxis
            dataKey="x"
            axisLine={false}
            tickLine={false}
            padding={{ left: 20, right: 0 }}
            tick={{ fill: '#748CAD', textTransform: 'upppercase' }}
            tickFormatter={(value) => `${value}`.toUpperCase()}
            style={{
              fontSize: '12px',
              fontFamily: 'Sofia Pro',
              color: '#748CAD',
              fontWeight: '500',
              textTransform: 'upppercase',
            }}
          />
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <Tooltip formatter={(value) => formatWithCommas(value)} />
          <Area
            type="monotone"
            dataKey="amount"
            stroke="#18379A"
            strokeWidth="2px"
            fillOpacity={1}
            fill="url(#colorUv)"
          />
        </AreaChart>
      </ResponsiveContainer>
    )}
  </>
);
export default function DashboardChart({
  summary,
  walletBalance,
  loadingGraphData,
  graphData,
  transactionAmount,
  transactionVolume,
}) {
  const [showWithdraw, setShowWithdraw] = React.useState(false);
  const [showTopUp, setShowTopUp] = React.useState(false);
  const directionIconsMap = {
    NEUTRAL: null,
    UPWARD: <ArrowUpIcon style={{ marginLeft: '-8px', marginTop: '-5px' }} />,
    DOWNWARD: (
      <ArrowDownIcon style={{ marginLeft: '-8px', marginTop: '-5px' }} />
    ),
  };
  const formattedData = Object.entries(graphData?.data || {}).reduce(
    (acc, [key, value], i) => {
      acc.push({ x: key, amount: value });
      return acc;
    },
    [],
  );
  const user = useSelector((state) => state.common.user.merchantUser);
  const isOwner = user.role === OWNER;
  const isSupportOfficer = user.role === SUPPORT_OFFICER;

  return (
    <>
      <WithdrawModal
        drawerActive={showWithdraw}
        setDrawerActive={setShowWithdraw}
      />
      <TopUpModal drawerActive={showTopUp} setDrawerActive={setShowTopUp} />
      <div className={styles.chart}>
        <div className={styles.left}>
          <div className={styles.leftGroup}>
            <div className={styles.group}>
              {transactionAmount.inProgress ? (
                <Fetching height="20px" />
              ) : (
                <>
                  <p className={styles.label}>Total value</p>
                  <div>
                    <p className={styles.amount}>
                      <span className={styles.currency}>₦</span>
                      <span>{formatWithCommas(transactionAmount.value)}</span>
                      <span className={styles.currency}>.00</span>
                    </p>
                    <p
                      className={classname(
                        styles.trend,
                        transactionAmount.trendDirection === 'UPWARD'
                          ? styles.up
                          : '',
                        transactionAmount.trendDirection === 'DOWNWARD'
                          ? styles.down
                          : '',
                      )}
                    >
                      {transactionAmount.trendDirection !== 'NEUTRAL' && (
                        <>
                          {directionIconsMap[transactionAmount.trendDirection]}
                        </>
                      )}
                      <span>{transactionAmount.trendValue}%</span>
                    </p>
                  </div>
                </>
              )}
            </div>
            <div className={styles.group}>
              {transactionVolume.inProgress ? (
                <Fetching height="20px" />
              ) : (
                <>
                  <p className={styles.label}>Total volume</p>
                  <p className={styles.amount}>
                    <span>{formatWithCommas(transactionVolume.value)}</span>
                  </p>
                  <p
                    className={classname(
                      styles.trend,
                      transactionVolume.trendDirection === 'UPWARD'
                        ? styles.up
                        : '',
                      transactionVolume.trendDirection === 'DOWNWARD'
                        ? styles.down
                        : '',
                    )}
                  >
                    {transactionVolume.trendDirection !== 'NEUTRAL' && (
                      <>{directionIconsMap[transactionVolume.trendDirection]}</>
                    )}
                    <span>{transactionVolume.trendValue}%</span>
                  </p>
                </>
              )}
            </div>

            {!isSupportOfficer && (
              <div className={`${styles.group} ${styles.mobile}`}>
                <p className={styles.label}>Available balance</p>
                <div
                  className={classname(
                    styles.amount,
                    globalStyles.justifyContentBetween,
                  )}
                >
                  <div>
                    <span className={styles.currency}>₦</span>
                    <span>{formatWithCommas(walletBalance)}</span>
                    <span className={styles.currency}>.00</span>
                  </div>
                  {isOwner && (
                    <div className={styles.buttons}>
                      <button
                        className={styles.button}
                        style={{ width: '100%' }}
                        type="button"
                        onClick={() => setShowWithdraw(true)}
                      >
                        Withdraw
                      </button>
                      <button
                        type="button"
                        style={{ width: '100%' }}
                        className={styles.button}
                        onClick={() => setShowTopUp(true)}
                      >
                        Top Up
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className={styles.group} />
          </div>
          <div className={styles.chartgroup}>
            <Chart data={formattedData} loadingGraphData={loadingGraphData} />
          </div>
        </div>
        <div className={styles.right}>
          {!isSupportOfficer && (
            <div className={styles.group}>
              <p className={styles.label}>Available balance</p>
              <p className={styles.amount}>
                <span className={styles.currency}>₦</span>
                <span>{formatWithCommas(walletBalance)}</span>
              </p>
            </div>
          )}
          {isOwner && (
            <div className={styles.buttons}>
              <Button
                className={styles.button}
                onClick={() => setShowWithdraw(true)}
                style={{ width: '100%' }}
                text="Withdraw"
              />

              <Button
                className={styles.button}
                onClick={() => setShowTopUp(true)}
                style={{ width: '100%' }}
                text="  Top Up"
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
